import { TransactionApiUrls } from "@/modules/transaction/common/enum";
import type {
  ICreateCurrencyExchangeRequest,
  ICurrencyExchange,
  IExchangeRatePersonalizedRequest,
  ISaveAsTemplate,
  IUpdateCurrencyExchangeRequest,
  TExchangeRatePersonalizedResponse,
} from "@/modules/transaction/common/types";
import { generateQuery, withIdempotency } from "@/shared/helpers";
import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "app/axios";

export const currencyExchangeApi = createApi({
  reducerPath: "currencyExchangeApi",
  baseQuery: axiosBaseQuery(),
  endpoints: (builder) => ({
    createCurrencyExchange: builder.mutation<string, ICreateCurrencyExchangeRequest>(
      withIdempotency<ICreateCurrencyExchangeRequest>(
        TransactionApiUrls.CurrencyExchangeV2
      )),
    updateCurrencyExchange: builder.mutation<string, IUpdateCurrencyExchangeRequest>({
      query: ({ data, id }) => ({
        url: `${TransactionApiUrls.CurrencyExchangeV2}/${id}`,
        method: "PUT",
        data,
      }),
    }),
    getCurrencyExchangeTransfer: builder.query<ICurrencyExchange, {
      id: string
    }>({
      query: ({ id }) => ({
        url: `${TransactionApiUrls.CurrencyExchange}/${id}`,
      }),
    }),
    getExchangeRatePersonalized: builder.query<
      TExchangeRatePersonalizedResponse,
      IExchangeRatePersonalizedRequest
    >({
      query: ({ sellCurrency, buyCurrency }) => {
        const query = generateQuery({ sellCurrency, buyCurrency });
        const url = `${TransactionApiUrls.PersonalizedExchangeRate}?${query}`;
        return { url };
      },
    }),
    saveCurrencyExchangeAsTemplate: builder.mutation<string, ISaveAsTemplate>({
      query: ({ id, name, idempotencyKey }) => ({
        url: `${TransactionApiUrls.CurrencyExchange}/${id}/Templates`,
        method: "POST",
        headers: { "Idempotency-Key": idempotencyKey },
        data: { name },
      }),
    }),
  }),
});

export const {
  useCreateCurrencyExchangeMutation,
  useUpdateCurrencyExchangeMutation,
  useGetCurrencyExchangeTransferQuery,
  useLazyGetExchangeRatePersonalizedQuery,
  useSaveCurrencyExchangeAsTemplateMutation,
} = currencyExchangeApi;
